import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AiSalesExplanationRecord {
  id: string;
  summary: string;
  factorsJson: string;
  confidence: number;
}

export interface AiSalesRecommendationRecord {
  id: string;
  recommendationType: string;
  entityType: string;
  entityId?: string | null;
  score: number;
  confidence: number;
  modelVersion: string;
  correlationId: string;
  idempotencyKey: string;
  status: string;
  inputSnapshotJson: string;
  outputJson: string;
  featureSnapshotId?: string | null;
  createdAtUtc: string;
  explanation?: AiSalesExplanationRecord | null;
}

export interface AiSalesRecommendationDetailRecord {
  recommendation: AiSalesRecommendationRecord;
  overrides: AiSalesOverrideRecord[];
  feedback: AiSalesFeedbackRecord[];
  conversations: AiSalesConversationRecord[];
}

export interface AiSalesOverrideRecord {
  id: string;
  decision: string;
  reason: string;
  actorUserId: string;
  auditTraceJson: string;
  createdAtUtc: string;
}

export interface AiSalesFeedbackRecord {
  id: string;
  outcome: string;
  score?: number | null;
  notes?: string | null;
  createdAtUtc: string;
}

export interface AiSalesConversationRecord {
  id: string;
  entityType: string;
  entityId: string;
  turnIndex: number;
  role: string;
  content: string;
  summaryJson?: string | null;
  createdAtUtc: string;
}

export interface AiSalesModelRecord {
  id: string;
  modelKey: string;
  modelVersion: string;
  provider: string;
  status: string;
  configJson: string;
  createdAtUtc: string;
}

export interface CreateAiSalesRecommendationPayload {
  recommendationType: string;
  entityType: string;
  entityId?: string | null;
  score: number;
  confidence: number;
  modelVersion: string;
  correlationId: string;
  idempotencyKey: string;
  inputSnapshotJson: string;
  outputJson: string;
  explanation: { summary: string; factorsJson?: string; confidence: number };
}

export interface AiSalesForecastPayload {
  forecastType: string;
  modelVersion?: string;
  inputSnapshotJson?: string;
  linkRecommendationId?: string | null;
}

export interface AiSalesForecastResult {
  snapshotId: string;
  forecastType: string;
  modelVersion: string;
  inputSnapshotJson: string;
  outputJson: string;
  linkedRecommendationId?: string | null;
}

export interface AiSalesFraudEvaluatePayload {
  orderTotalAmount?: number;
  totalDiscountAmount?: number;
  basisAmount?: number;
  commissionAmount?: number;
  returnQuantityRatio?: number;
  consignmentUnsettledDays?: number;
  entityId?: string | null;
  entityType?: string | null;
}

export interface AiSalesFraudEvaluateResult {
  alert: boolean;
  autoBlock: boolean;
  threshold: number;
  maxSignalScore: number;
  signals: { code: string; score: number; message: string }[];
  modelVersion: string;
  outputJson: string;
}

@Injectable({ providedIn: 'root' })
export class SalesAiApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/ai-sales`;

  listRecommendations(filters?: {
    entityType?: string;
    entityId?: string;
    recommendationType?: string;
  }): Observable<AiSalesRecommendationRecord[]> {
    let params = new HttpParams();
    if (filters?.entityType) params = params.set('entityType', filters.entityType);
    if (filters?.entityId) params = params.set('entityId', filters.entityId);
    if (filters?.recommendationType) params = params.set('recommendationType', filters.recommendationType);
    return this.http.get<AiSalesRecommendationRecord[]>(`${this.base}/recommendations`, { params });
  }

  getRecommendation(id: string): Observable<AiSalesRecommendationDetailRecord> {
    return this.http.get<AiSalesRecommendationDetailRecord>(`${this.base}/recommendations/${id}`);
  }

  createRecommendation(payload: CreateAiSalesRecommendationPayload): Observable<AiSalesRecommendationRecord> {
    return this.http.post<AiSalesRecommendationRecord>(`${this.base}/recommendations`, payload);
  }

  overrideRecommendation(id: string, payload: { decision: string; reason: string; auditTraceJson?: string }): Observable<AiSalesOverrideRecord> {
    return this.http.post<AiSalesOverrideRecord>(`${this.base}/recommendations/${id}/override`, payload);
  }

  submitFeedback(id: string, payload: { outcome: string; score?: number; notes?: string }): Observable<AiSalesFeedbackRecord> {
    return this.http.post<AiSalesFeedbackRecord>(`${this.base}/recommendations/${id}/feedback`, payload);
  }

  listModels(): Observable<AiSalesModelRecord[]> {
    return this.http.get<AiSalesModelRecord[]>(`${this.base}/models`);
  }

  registerModel(payload: { modelKey: string; modelVersion: string; provider?: string; configJson?: string }): Observable<AiSalesModelRecord> {
    return this.http.post<AiSalesModelRecord>(`${this.base}/models`, payload);
  }

  forecast(payload: AiSalesForecastPayload): Observable<AiSalesForecastResult> {
    return this.http.post<AiSalesForecastResult>(`${this.base}/forecast`, payload);
  }

  evaluateFraud(payload: AiSalesFraudEvaluatePayload): Observable<AiSalesFraudEvaluateResult> {
    return this.http.post<AiSalesFraudEvaluateResult>(`${this.base}/fraud-detection/evaluate`, payload);
  }

  listConversations(entityType: string, entityId: string): Observable<AiSalesConversationRecord[]> {
    const params = new HttpParams().set('entityType', entityType).set('entityId', entityId);
    return this.http.get<AiSalesConversationRecord[]>(`${this.base}/conversations`, { params });
  }

  createConversation(payload: {
    entityType: string;
    entityId: string;
    role: string;
    content: string;
    summaryJson?: string;
  }): Observable<AiSalesConversationRecord> {
    return this.http.post<AiSalesConversationRecord>(`${this.base}/conversations`, payload);
  }

  /** Server-side inference + persisted recommendation (no static payload from Angular). */
  suggestCopilot(payload: {
    recommendationType: string;
    entityType: string;
    entityId?: string | null;
    contextJson?: string;
  }): Observable<AiSalesRecommendationRecord> {
    return this.http.post<AiSalesRecommendationRecord>(`${this.base}/copilot/suggest`, payload);
  }
}
